import { watch, onBeforeUnmount } from "vue";
import { useToast } from "primevue/usetoast";
import { useHttpErrors } from "@/composables/useHttpErrors";
import { logger } from "@/utils/logger";

const DEFAULT_TOAST_LIFE = 8000;

interface ErrorEntry {
  id?: string;
  error?: {
    httpStatusCode?: number | string;
    defaultUserMessage?: string;
    errorCode?: string;
    developerMessage?: string;
  };
  method?: string;
  url?: string;
}

function resolveSeverityFromStatus(errorEntry: ErrorEntry): string {
  const statusCode = Number(errorEntry?.error?.httpStatusCode || 500);

  if (statusCode >= 500) {
    return "error";
  }

  if (statusCode >= 400) {
    return "warn";
  }

  return "info";
}

function formatSummary(errorEntry: ErrorEntry): string {
  return errorEntry?.error?.defaultUserMessage || "Request failed";
}

function formatDetail(errorEntry: ErrorEntry): string {
  const summary = errorEntry?.error?.defaultUserMessage;

  if (summary) {
    return "";
  }

  const method = errorEntry?.method || "REQUEST";
  const url = errorEntry?.url || "";
  const location = url ? `${method} ${url}` : method;
  const errorCode = errorEntry?.error?.errorCode;
  const developerMessage = errorEntry?.error?.developerMessage;

  if (errorCode) {
    return `${location}\nError code: ${errorCode}`;
  }

  if (developerMessage) {
    return `${location}\n${developerMessage}`;
  }

  return location;
}

interface HttpErrorToastServiceOptions {
  life?: number;
  group?: string;
  stickyStatuses?: string[];
  severityResolver?: (errorEntry: ErrorEntry) => string;
  summaryFormatter?: (errorEntry: ErrorEntry) => string;
  detailFormatter?: (errorEntry: ErrorEntry) => string;
}

export function useHttpErrorToastService(
  options: HttpErrorToastServiceOptions = {},
) {
  const {
    life = DEFAULT_TOAST_LIFE,
    group,
    stickyStatuses = ["400"],
    severityResolver = resolveSeverityFromStatus,
    summaryFormatter = formatSummary,
    detailFormatter = formatDetail,
  } = options;
  const stickyStatusSet = new Set(
    (stickyStatuses || []).map((status) => status?.toString()).filter(Boolean),
  );
  let toast: ReturnType<typeof useToast> | null = null;
  try {
    toast = useToast();
  } catch (error) {
    logger.error("Error initializing toast service:", error);
    return {
      stop() {},
      clear() {},
    };
  }
  const { errors, lastSuccessfulRequestToken } = useHttpErrors();
  const displayedErrorIds = new Set();

  function clearToasts() {
    if (!toast) return;
    if (group && typeof toast.removeGroup === "function") {
      toast.removeGroup(group);
    } else if (typeof toast.removeAllGroups === "function") {
      toast.removeAllGroups();
    }
    displayedErrorIds.clear();
  }

  let unwatchErrors: ReturnType<typeof watch> | null = watch(
    errors,
    (currentErrors: ErrorEntry[] = []) => {
      // Показываем только последнюю ошибку
      const latestError = currentErrors.length > 0 ? currentErrors[0] : null;

      if (!latestError) {
        // Если ошибок нет, очищаем все тосты
        clearToasts();
        return;
      }

      // Если эта ошибка уже отображалась, пропускаем
      if (latestError.id && displayedErrorIds.has(latestError.id)) {
        return;
      }

      // Очищаем предыдущие тосты перед показом новой ошибки
      clearToasts();

      // Добавляем ID новой ошибки
      if (latestError.id) {
        displayedErrorIds.add(latestError.id);
      }

      const severity = severityResolver(latestError);
      const summary = summaryFormatter(latestError);
      const detail = detailFormatter(latestError);
      const statusCodeKey = latestError?.error?.httpStatusCode?.toString();
      const isSticky = statusCodeKey
        ? stickyStatusSet.has(statusCodeKey)
        : false;

      if (toast) {
        toast.add({
          severity,
          summary,
          detail,
          group,
          life: isSticky ? 0 : life,
          closable: true,
        });
      }
    },
    { deep: true, immediate: true },
  );

  let unwatchSuccess: ReturnType<typeof watch> | null = watch(
    lastSuccessfulRequestToken,
    (token) => {
      if (!token) {
        return;
      }

      clearToasts();
    },
    { immediate: false },
  );

  function stop({ shouldClear = false }: { shouldClear?: boolean } = {}) {
    if (unwatchErrors) {
      unwatchErrors();
      unwatchErrors = null;
    }

    if (unwatchSuccess) {
      unwatchSuccess();
      unwatchSuccess = null;
    }

    if (shouldClear) {
      clearToasts();
    }
  }

  onBeforeUnmount(() => stop({ shouldClear: true }));

  return { stop, clear: clearToasts };
}
